/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Prop, Watch} from 'vue-property-decorator';
import {
    AchievementInterface,
    ChallengeAchievementInterface, CommunityChallengeAchievementsRequestInterface,
    CommunityChallengeAlmanac, RewardAchievement, RewardAchievementSet, RewardType,
    SpeciesAchievementInterface,
    TrophyCabinetMode, TrophyGrade,
} from '@/store/modules/trophy-cabinet/types';
import {mixins} from 'vue-class-component';
import GameData from '@/mixins/GameData';
import {Action, Getter, Mutation} from 'vuex-class';
import {JsonLdCollection, LoadStatus, LoadStatusInterface} from '@/store/types';
import OverlayDisable from '@/components/OverlayDisable/index.vue';
import SpeciesAchievementListing from '@/components/SpeciesAchievementListing/index.vue';
import CommunityChallengeListing from '@/components/CommunityChallengeListing/index.vue';
import {
    CommunityChallengeInterface,
    CommunityChallengeMilestoneInterface,
} from '@/store/modules/community-challenge/types';
import Utils from '@/services/utils';
import {AudioApiCallInterface} from '@/store/modules/game/types';
import Scroll from '@/mixins/Scroll';

/**
 * Trophy cabinet view.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'v-trophy-cabinet',
    components: {
        'c-overlay-disable': OverlayDisable,
        'c-species-achievement-listing': SpeciesAchievementListing,
        'c-community-challenge-listing': CommunityChallengeListing,
    },
})
export default class TrophyCabinet extends mixins(GameData, Scroll) {
    @Prop(String)
    public type!: string;

    @Action('callAudioApi', {namespace: 'gameModule'})
    public callAudioApi!: (audioApiCall: AudioApiCallInterface) => void;

    @Action('getChallenges', {namespace: 'communityChallengesModule'})
    public getChallenges!: () => Promise<JsonLdCollection<CommunityChallengeInterface>>;

    @Action('getSpeciesAchievements', {namespace: 'trophyCabinetModule'})
    public getSpeciesAchievements!: () => Promise<JsonLdCollection<SpeciesAchievementInterface>>;

    @Action('getChallengeAchievements', {namespace: 'trophyCabinetModule'})
    public getChallengeAchievements!:
        (params: CommunityChallengeAchievementsRequestInterface) => Promise<ChallengeAchievementInterface>;

    @Action('setMode', {namespace: 'trophyCabinetModule'})
    public setMode!: (mode: TrophyCabinetMode) => void;

    @Getter('challenges', {namespace: 'communityChallengesModule'})
    public challenges!: CommunityChallengeInterface[];

    @Getter('alamanac', {namespace: 'trophyCabinetModule'})
    public alamanac!: CommunityChallengeAlmanac[];

    @Getter('mode', {namespace: 'trophyCabinetModule'})
    public mode!: TrophyCabinetMode;

    @Mutation('setContributionAndRewards', {namespace: 'communityChallengesModule'})
    public setContributionAndRewards!: (
        {challenge, contribution, trophy, rewards}:
        {challenge: CommunityChallengeInterface, contribution: number, trophy: TrophyGrade,
            rewards: RewardAchievementSet, percentileContribution: number},
    ) => void;

    @Getter('failed', {namespace: 'trophyCabinetModule'})
    public failedTrophyCabinet!: boolean;

    public includeFavourites: boolean = false;
    public loading: LoadStatusInterface = new LoadStatus();

    @Watch('type')
    public routeChange(): void {
        const routes: string[] = Object.values(TrophyCabinetMode);
        this.setMode(routes.includes(this.type) ? this.type as TrophyCabinetMode : TrophyCabinetMode.CC);
    }

    /**
     * @param {CommunityChallengeInterface} challenge
     * @return {boolean}
     */
    public active(challenge: CommunityChallengeInterface): boolean {
        return challenge.commenced && !challenge.expired;
    }

    /**
     * Configure route.
     */
    public async created(): Promise<void> {
        this.routeChange();
        try {
            this.loading.loading = true;

            // initial load
            await Promise.all([
                this.getChallenges(),
                this.getSpeciesAchievements(),
            ]);

            // determine the challenges and milestones to use
            const params: CommunityChallengeAchievementsRequestInterface =  {
                challenges: this.challenges.map((item: CommunityChallengeInterface) => +item.id),
                milestones: this.challenges.map((item: CommunityChallengeInterface) => (item.milestone || [])
                    .map((ms: CommunityChallengeMilestoneInterface) => +Utils.extractIriId(ms['@id'])))
                    .flat(1),
                active: !!this.challenges[0] && this.active(this.challenges[0]) ? +this.challenges[0].id : undefined,
            };

            // load trophies
            const achievements: ChallengeAchievementInterface = await this.getChallengeAchievements(params);

            // apply changes
            for (const challenge of this.challenges) {
                // find almanac contribution value
                let contribution: number = 0;
                if (params.active === +challenge.id) {
                    contribution = achievements.contribution;
                } else {
                    const almanac: CommunityChallengeAlmanac|undefined = achievements.almanac.find(
                    (item: CommunityChallengeAlmanac) => {
                        return challenge['@id'] === item.challenge;
                    });

                    contribution = (!almanac || !almanac.total) ? 0 : almanac.total;
                }

                // find percentile contribution
                const percentileContribution: number = this.percentageContribution(challenge, contribution);

                const trophy: TrophyGrade = this.findTrophyGrade(challenge, contribution);
                const rewards: RewardAchievementSet =
                    this.findRewardSet(challenge.milestone || [], achievements.achievement);
                this.setContributionAndRewards({challenge, contribution, trophy, rewards, percentileContribution});
            }
            this.loading.loading = false;
            this.loading.success = true;
        } catch (e) {
            this.loading.loading = false;
            this.loading.error = true;
        }
    }

    /**
     * Percentage contribution of challenge.
     * @param {CommunityChallengeInterface} challenge
     * @param {number} contribution
     * @return {number}
     */
    public percentageContribution(challenge: CommunityChallengeInterface, contribution: number): number {
        contribution = contribution || 0;
        return +Object.keys(challenge.percentile).reduce((reducer: string, key: string) => {
            if (contribution >= challenge.percentile[key]) {
                reducer = ((1 - parseFloat(key)) * 100).toFixed(0);
            }
            return reducer;
        }, '100');
    }

    /**
     * Find the grade of the trophy (gold, silver, brozne, none) for challenge.
     * @param {CommunityChallengeInterface} challenge
     * @param {number} contribution
     * @return {TrophyGrade}
     */
    public findTrophyGrade(challenge: CommunityChallengeInterface, contribution: number): TrophyGrade {
        const items: string[] = Object.keys(challenge.percentile);
        if (items.length < 3 || 0 === contribution) {
            return TrophyGrade.NONE;
        }

        if (contribution >= challenge.percentile[items[items.length - 1]]) {
            return TrophyGrade.GOLD;
        }

        if (contribution >= challenge.percentile[items[items.length - 2]]) {
            return TrophyGrade.SILVER;
        }

        if (contribution >= challenge.percentile[items[items.length - 3]]) {
            return TrophyGrade.BRONZE;
        }

        return TrophyGrade.NONE;
    }

    /**
     * Find reward set for challenge milestones.
     * @param {CommunityChallengeMilestoneInterface[]} milestones
     * @param {AchievementInterface[]} achievements
     * @return {RewardAchievementSet}
     */
    public findRewardSet(
        milestones: CommunityChallengeMilestoneInterface[],
        achievements: AchievementInterface[],
    ): RewardAchievementSet {
        const types: string[] = Object.values(RewardType);
        return milestones.reduce(
            (reducer: RewardAchievementSet, item: CommunityChallengeMilestoneInterface) => {
                const found: boolean = 0 <= achievements.findIndex((achievement: AchievementInterface) => {
                    return achievement.milestone === item['@id'];
                });

                for (const reward of item.rewards) {
                    if (!types.includes(reward['@type'])) {
                        continue;
                    }

                    if (!reducer[reward['@type']]) {
                        reducer[reward['@type']] = {};
                    }

                    if (item.type === 'personal' && !(reducer[reward['@type']] as RewardAchievement).personal) {
                        (reducer[reward['@type']] as RewardAchievement).personal = found;
                    }

                    if (item.type === 'completion' && !(reducer[reward['@type']] as RewardAchievement).completion) {
                        (reducer[reward['@type']] as RewardAchievement).completion = found;
                    }
                }
                return reducer;
            }, {});
    }


    /**
     * Title for selected mode.
     * @return {string}
     */
    public get title(): string {
        return this.$i18n.translate(`achievements.${this.mode}.title`) || '';
    }

    /**
     * Check for species achievement mode.
     * @return {boolean}
     */
    public get isTrophyCabinetMode(): boolean {
        return this.mode !== TrophyCabinetMode.CC && this.loading.success;
    }

    /**
     * Check for species challenge mode.
     * @return {boolean}
     */
    public get isCcMode(): boolean {
        return this.mode === TrophyCabinetMode.CC && this.loading.success;
    }
}

